import {
    useCallback,
    useState,
    type CSSProperties,
    type ReactNode,
} from 'react';

import { cn } from '@rocco/utils/cn';
import { getImageAttrs } from '~/modules/images';
import { TileOverlayWithFollowSelector } from '~/modules/moodboards/components';
import type {
    TileActionInfo,
    TileVisitWebsiteConfig,
} from '~/modules/moodboards/components/TileActionPanel';
import type { PinItemType } from '~/modules/moodboards/type';
import type { TileEnquiryConfig } from '~/modules/enquiry/hooks/use-tile-enquiry';
import type { ShareOptions } from '~/modules/social-sharing/utils/sharing';

/**
 * Marks the media box so a tile's click handler can find the image it is
 * actually showing — the Save overlay and logo badge are `<img>`s too, and
 * either would be the wrong thing to carry into the morph.
 */
export const DETAIL_TILE_MEDIA_CLASS = 'detail-tile-media';

interface DetailTileMediaProps {
    src?: string;
    alt?: string;
    /**
     * The tile's rendered width as a `sizes` string (see `getImagesSizes`).
     * Set it to serve the image through the CDN loader at the size it lands
     * at; without it the original is served untouched.
     */
    sizes?: string;
    /** Aspect-ratio + background classes for the media box, e.g. `aspect-[446/550]`. */
    className?: string;
    /**
     * Inline style for the media box. Carries the view-transition name claim
     * when this tile is the one morphing into a detail hero.
     */
    style?: CSSProperties;
    imgClassName?: string;
    /**
     * Pin id + type enable the hover Save (FollowSelector / design-board)
     * overlay. Omit them for media that isn't a pinnable directory item (team
     * photos, the about feature image, etc.).
     */
    pinId?: number;
    pinType?: PinItemType;
    /** Library item id for the pin; falls back to `pinId` when unset. */
    libraryItemId?: number;
    /** Enquiry config for the panel's Enquire button; omit for More-only. */
    enquiry?: TileEnquiryConfig | null;
    /** Share target for the panel's More → Share item. */
    share?: ShareOptions | null;
    /** "Visit site" for the panel, when the surface fetched a website. */
    visitWebsite?: TileVisitWebsiteConfig | null;
    /** Item info for the panel's mobile action sheet header. */
    tileInfo?: TileActionInfo;
    /** Extra absolute overlays — badges, gradients, etc. */
    children?: ReactNode;
}

/**
 * The editorial tile image box for the revamp detail pages: a full-bleed cover
 * image plus the shared hover Save overlay ({@link TileOverlayWithFollowSelector}).
 *
 * It composes the *canonical* follow behaviour rather than re-implementing it —
 * the same overlay the Rocco marketplace tiles and the products-page editorial
 * tiles use — so the save/board logic stays single-source. The right-click admin
 * menu is composed one level up at the tile (see `DetailTileAdminMenu`), because
 * it wraps the whole clickable tile, not just the image.
 *
 * ── MIGRATION PATH ─────────────────────────────────────────────────────────
 * The editorial tile style is very likely to replace the rounded Rocco
 * marketplace tile site-wide (category / search / homepage) once the detail
 * revamp lands. When that happens, DO NOT fork a `variant="editorial"` through
 * the 4–5 per-type Rocco tiles (`ProjectTile`, `ProductTile`, …) — that forks
 * complex, heavily-shared components and two design languages end up fighting in
 * one file. Instead converge on THIS primitive:
 *   1. Lift `DetailTileMedia` (+ `DetailTileAdminMenu`) into `@rocco/ui/tile-shared`
 *      so it sits beside `TileOverlay` / `AdminContextMenu`.
 *   2. Reskin each per-type Rocco tile to compose `DetailTileMedia` for its image
 *      box + the existing overlay/admin units — deleting the bespoke hover-rail
 *      markup, keeping the per-type metadata rows (price, beds, brand, …).
 *   3. Drive it from the existing `TileViewModel` (it already carries
 *      `adminStatusInfo`, follow state and the ids these overlays need), so the
 *      swap is presentation-only — no behaviour rewrite, no data plumbing.
 * Because every behaviour already lives in a shared unit, convergence is a
 * styling merge, not a re-implementation. Keep new editorial tiles flowing
 * through here so there is one place to migrate from.
 * ───────────────────────────────────────────────────────────────────────────
 */
export const DetailTileMedia = ({
    src,
    alt,
    sizes,
    className,
    style,
    imgClassName,
    pinId,
    pinType,
    libraryItemId,
    visitWebsite,
    enquiry,
    share,
    tileInfo,
    children,
}: DetailTileMediaProps) => {
    // Fade the image in once it decodes so lazily-loaded tiles (notably the
    // ones infinite scroll appends) ease over the placeholder instead of
    // hard-cutting from grey to photo. A cached/SSR image can finish before
    // React's synthetic `onLoad` binds, so reveal synchronously when the node
    // is already `complete`; otherwise attach native listeners (no React
    // event-timing race). `error` reveal guarantees a broken image never stays
    // invisible.
    const [loaded, setLoaded] = useState(false);
    const imgRef = useCallback((node: HTMLImageElement | null) => {
        if (!node) return;
        if (node.complete) {
            setLoaded(true);
            return;
        }
        const reveal = () => setLoaded(true);
        node.addEventListener('load', reveal, { once: true });
        node.addEventListener('error', reveal, { once: true });
        // A cached image can complete in the gap between the check above and
        // the listeners attaching, firing `load` with no listener — re-check.
        if (node.complete) reveal();
    }, []);
    return (
        <div
            className={cn(
                DETAIL_TILE_MEDIA_CLASS,
                'group relative w-full overflow-hidden rounded-tile',
                className,
            )}
            style={style}
        >
            {src ? (
                <img
                    ref={imgRef}
                    {...(sizes ? getImageAttrs(src, { sizes }) : { src })}
                    alt={alt}
                    decoding="async"
                    className={cn(
                        'absolute inset-0 size-full object-cover',
                        'transition-opacity duration-500 ease-out motion-reduce:transition-none',
                        loaded ? 'opacity-100' : 'opacity-0',
                        imgClassName,
                    )}
                    loading="lazy"
                />
            ) : null}
            {children}
            {pinId && pinType ? (
                <TileOverlayWithFollowSelector
                    pinId={pinId}
                    pinType={pinType}
                    libraryItemId={libraryItemId}
                    enquiry={enquiry}
                    visitWebsite={visitWebsite}
                    share={share}
                    tileInfo={tileInfo}
                />
            ) : null}
        </div>
    );
};
